/**
 * VOX - typy a konfiguracia.
 *
 * Kriteria su JEDEN objekt, ktory zije cely rozhovor. Kazda veta sa do neho
 * zlucuje, neprepisuje ho cely. null v patchi znamena ODOBRANIE kriteria.
 */

export type VoxTxnType = 'SALE' | 'RENT';
export type VoxPropertyType = 'HOUSE' | 'APARTMENT' | 'LAND' | 'RECREATIONAL' | 'OTHER';

export type VoxTransactionType = 'SALE' | 'RENT';
export type VoxPropertyCategory = 'APARTMENT' | 'HOUSE' | 'RECREATIONAL' | 'LAND' | 'OTHER';
export type VoxRoomCategory =
    | 'STUDIO'
    | 'ONE_OR_ONE_HALF'
    | 'TWO_OR_TWO_HALF'
    | 'THREE_OR_THREE_HALF'
    | 'FOUR_OR_FOUR_HALF'
    | 'FIVE_OR_MORE';
export type VoxCondition =
    | 'NEW_BUILD'
    | 'FULL_RECONSTRUCTION'
    | 'PARTIAL_RECONSTRUCTION'
    | 'ORIGINAL'
    | 'UNDER_CONSTRUCTION';

export type VoxRange = { min?: number | null; max?: number | null };

/**
 * Kriteria v tvare podla specifikacie vox-search-criteria.json (Julius).
 *
 * Odchylka: `location` ma namiesto `municipalityId` z ciselnika nazov obce tak,
 * ako zaznel. Ciselnik (GET /api/v1/locations/search) zatial nemame k dispozicii;
 * ked pribudne, doplni sa prekladac nazov -> id a tvar bude uplny.
 */
export type VoxCriteria = {
    transactionType?: VoxTransactionType | null;
    propertyCategory?: VoxPropertyCategory[] | null;
    location?: { municipalityName?: string[] | null; radiusKm?: number | null } | null;
    price?: VoxRange | null;
    usableArea?: VoxRange | null;
    landArea?: VoxRange | null;
    roomCount?: VoxRange | null;
    roomCategory?: VoxRoomCategory[] | null;
    propertyCondition?: VoxCondition[] | null;
    additionalSpaces?: string[] | null;
    features?: { equipment?: string[] | null } | null;
    media?: string[] | null;
};

/** Zariadenie v okoli ponuky - skola, skolka, zastavka a podobne. */
export type VoxOkolieMiesto = {
    nazov: string | null;
    /** vzdialenost v metroch */
    m: number;
    /** minuty pesou chodze */
    min: number;
    lat?: number;
    lng?: number;
};

export type VoxOkolieKategoria = {
    label: string;
    pocet: number;
    najblizsie: VoxOkolieMiesto[];
};

/** Ponuka tak, ako ju vracia /vox-api/search. Zodpoveda tabulke vox.listings. */
export type VoxListing = {
    id: number;
    code: string;
    property_type: VoxPropertyType;
    txn_type: VoxTxnType;
    city: string;
    street: string | null;
    price: number;
    area: number | null;
    lat: string | number | null;
    lng: string | number | null;
    photos: string[];
    details?: string | null;
    /** Okolie a hodnotenie lokality - doplna sa na pozadi po zalozeni ponuky. */
    nearby?: {
        okolie: Record<string, VoxOkolieKategoria>;
        hodnotenie?: { celkom: number; oblasti: { key: string; label: string; hviezd: number }[] };
        zhrnutie?: string | null;
    } | null;
    created_at?: string;
};

/** Nastavenie hlasu a krajenia reci. Zdroj: GET /vox-api/settings. */
export type VoxSettings = {
    voice: string;
    instructions: string;
    model: string;
    speed: number;
    format: string;
    /** Ako dlho musi byt ticho, aby sa veta povazovala za dokoncenu. */
    silenceMs: number;
    /** Prah hlasitosti reci. Nizsie cislo = citlivejsie. */
    vadLevel: number;
    /** Po akom tichu sa rozhovor ukonci sam. */
    idleEndMs: number;
    /** Oslovenie po prichode na stranku. */
    greeting: string;
    /** Kratka veta pri stlaceni mikrofonu. */
    startPhrase: string;
    voices: string[];
    models: string[];
};

export type VoxRelax = { keys: string[]; label: string; count: number; hint: string };

export type VoxMessage = { role: 'user' | 'assistant' | 'system'; content: string };

/** Nazvy kriterii pre UI. Poradie urcuje poradie cipov. */
export const VOX_CRITERIA_LABELS: Record<string, string> = {
    transactionType: 'Transakcia',
    propertyCategory: 'Kategória',
    location: 'Lokalita',
    price: 'Cena',
    usableArea: 'Úžitková plocha',
    landArea: 'Plocha pozemku',
    roomCount: 'Počet izieb',
    roomCategory: 'Dispozícia',
    propertyCondition: 'Stav',
    additionalSpaces: 'Doplnkové priestory',
    features: 'Vybavenie',
    media: 'Médiá',
};

/**
 * Adresa VOX sluzby.
 *
 * Sluzba bezi mimo backendu portalu (CLAUDE.md zakazuje stavat serverovu vrstvu
 * pre VOX do portalu), ale je pripojena na TU ISTU domenu pod cestou /vox-api/.
 *
 * Preco same-origin a nie vlastna domena: portal ma v next.config.ts CSP
 * s connect-src 'self' a img-src 'self'. Cudzia domena by bola v prehliadaci
 * zablokovana a next.config.ts je chraneny subor. Same-origin cesta to riesi
 * bez zasahu do konfiguracie portalu a zaroven odpadaju CORS aj token
 * v prehliadaci - token pridava nginx na strane servera.
 */
export const VOX_API = '/vox-api';

/** Adresa fotiek testovacieho datasetu. */
export const VOX_PHOTOS = VOX_API + '/photos';
